import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowLeft,
  Download,
  Info,
  Loader2,
  Table as TableIcon,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { CATEGORICAL, CHART_AXIS, DEFAULT_CHART_MARGIN, formatChartCurrency } from '@/lib/theme'
import { formatCurrency } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import { useTtmRevenue } from '@/hooks/use-api'
import { fiscalLabel, fiscalYearLabel, monthLabel, shortMonthLabel } from '@/lib/fiscal'
import type { RevenueCategory } from '@/types'

const TTM_COLOR = CATEGORICAL.prussian.solid
// Powder is a fill-only hue in the brand palette (below 3:1 on white), so per
// theme.ts it carries a Prussian stroke when used as a chart fill.
const MONTHLY_COLOR = CATEGORICAL.powder.solid
const MONTHLY_STROKE = CATEGORICAL.prussian.solid
const PRIOR_COLOR = CATEGORICAL.clay.solid

const pctLabel = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`

/** A stat card. `delta` is rendered with an arrow and a text sign, never colour alone. */
function StatCard({
  title,
  value,
  hint,
  delta,
}: {
  title: string
  value: string
  hint?: string
  delta?: number | null
}) {
  const rising = delta !== null && delta !== undefined && delta >= 0
  const Arrow = rising ? TrendingUp : TrendingDown
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {delta !== null && delta !== undefined && (
          <p className="flex items-center gap-1 text-sm font-medium">
            <Arrow className="h-4 w-4" aria-hidden="true" />
            <span>{pctLabel(delta)}</span>
          </p>
        )}
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  )
}

export default function RevenueTrendReport() {
  const navigate = useNavigate()
  const [category, setCategory] = useState<RevenueCategory>('Software')
  const [showTable, setShowTable] = useState(false)

  const { data, isLoading, error } = useTtmRevenue({ category })

  // ---- TTM view: the monthly bars and the rolling line share one row per month.
  const ttmChartData = useMemo(() => {
    if (!data) return []
    // Only complete months, so a part-month cannot drag the final point down.
    return data.months
      .filter((m) => m.complete)
      .map((m) => ({
        key: m.month_key,
        label: shortMonthLabel(m.month_key),
        monthly: m.revenue,
        ttm: m.ttm,
      }))
  }, [data])

  // ---- Pace view: two fiscal years merged on the shared fiscal month index.
  const paceChartData = useMemo(() => {
    if (!data) return []
    const currentByIndex = new Map(data.pace.current.map((p) => [p.fiscal_month_index, p]))
    const priorByIndex = new Map(data.pace.prior.map((p) => [p.fiscal_month_index, p]))
    return Array.from({ length: 12 }, (_, i) => {
      const index = i + 1
      const current = currentByIndex.get(index)
      const prior = priorByIndex.get(index)
      return {
        index,
        label: fiscalLabel(index),
        // Null past the current month so the line stops rather than dropping to zero.
        current: current ? current.cumulative : null,
        prior: prior ? prior.cumulative : null,
      }
    })
  }, [data])

  const summary = data?.summary
  const currentFyIndex = useMemo(() => {
    if (!data || !summary?.paceThroughMonth) return null
    const row = data.months.find((m) => m.month_key === summary.paceThroughMonth)
    return row ? row.fiscal_month_index : null
  }, [data, summary])

  const handleExport = () => {
    if (!data) return
    exportToCSV({
      filename: `revenue-trend-${category.toLowerCase()}.csv`,
      data: data.months.map((m) => ({
        Month: m.month_key,
        Category: category,
        'Revenue (CAD)': m.revenue.toFixed(2),
        Deals: m.deal_count,
        'TTM (CAD)': m.ttm === null ? '' : m.ttm.toFixed(2),
        'Fiscal Year': fiscalYearLabel(m.fiscal_year),
        'Fiscal Month': fiscalLabel(m.fiscal_month_index),
        'FY Cumulative (CAD)': m.fy_cumulative.toFixed(2),
        Complete: m.complete ? 'Yes' : 'In progress',
      })),
    })
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" role="status" aria-live="polite">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <span className="ml-3 text-sm text-muted-foreground">Loading revenue trend…</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Could not load the revenue trend</CardTitle>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'The report did not return any data. Try reloading the page; if it keeps failing, the reporting service may be unavailable.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/reports')}>Back to reports</Button>
        </CardContent>
      </Card>
    )
  }

  const ttmDescription = summary?.ttmThroughMonth
    ? `Rolling twelve-month ${category.toLowerCase()} revenue for the 24 months ending ${monthLabel(summary.ttmThroughMonth)}. Latest value ${formatCurrency(summary.ttmLatest ?? 0)}, ${pctLabel(summary.ttmGrowthPct)} against the same window a year earlier.`
    : `Rolling twelve-month ${category.toLowerCase()} revenue.`

  const paceDescription = `Cumulative ${category.toLowerCase()} revenue within the fiscal year, ${fiscalYearLabel(data.pace.currentFiscalYear)} against ${fiscalYearLabel(data.pace.priorFiscalYear)}, both starting at zero on 1 March.`

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/reports')}
            aria-label="Back to reports"
          >
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Reports
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Software &amp; Services Revenue Trend</h1>
            <p className="max-w-3xl text-sm text-muted-foreground">
              Trailing-twelve-month revenue and fiscal-year pace, from Closed Won line items. Values
              in CAD, on the opportunity close date.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTable((v) => !v)}
            aria-pressed={showTable}
          >
            <TableIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            {showTable ? 'Hide data table' : 'Show data table'}
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      <Tabs value={category} onValueChange={(v) => setCategory(v as RevenueCategory)}>
        <TabsList aria-label="Product category">
          <TabsTrigger value="Software">Software</TabsTrigger>
          <TabsTrigger value="Services">Services</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={`TTM revenue${summary?.ttmThroughMonth ? ` to ${monthLabel(summary.ttmThroughMonth)}` : ''}`}
          value={formatCurrency(summary?.ttmLatest ?? 0)}
          delta={summary?.ttmGrowthPct ?? null}
          hint="Against the same twelve-month window one year earlier"
        />
        <StatCard
          title="Twelve months to a year ago"
          value={formatCurrency(summary?.ttmYearAgo ?? 0)}
          hint="The comparison window, shown so the growth figure can be checked"
        />
        <StatCard
          title={`${fiscalYearLabel(data.pace.currentFiscalYear)} to date`}
          value={formatCurrency(summary?.fyToDate ?? 0)}
          delta={summary?.paceDeltaPct ?? null}
          hint={
            summary?.paceThroughMonth
              ? `Through ${monthLabel(summary.paceThroughMonth)}, against ${formatCurrency(summary.priorFyToSameMonth ?? 0)} at the same point in ${fiscalYearLabel(data.pace.priorFiscalYear)}`
              : undefined
          }
        />
        <StatCard
          title="Month in progress"
          value={formatCurrency(summary?.monthInProgress?.revenue ?? 0)}
          hint={
            summary?.monthInProgress
              ? `${monthLabel(summary.monthInProgress.month_key)} so far. Excluded from the TTM line, because a part-month there would invent a downturn.`
              : undefined
          }
        />
      </div>

      <Tabs defaultValue="ttm">
        <TabsList aria-label="Chart view">
          <TabsTrigger value="ttm">TTM trend</TabsTrigger>
          <TabsTrigger value="pace">Fiscal-year pace</TabsTrigger>
        </TabsList>

        <TabsContent value="ttm">
          <Card>
            <CardHeader>
              <CardTitle>Trailing twelve months</CardTitle>
              <CardDescription>
                Each point sums the twelve months ending there, so it moves only by the month added
                minus the month dropped — one large deal cannot spike it. The bars behind are the
                individual months the line is smoothing.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[420px] w-full" role="img" aria-label={ttmDescription}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={ttmChartData} margin={DEFAULT_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS} opacity={0.2} />
                    <XAxis
                      dataKey="label"
                      stroke={CHART_AXIS}
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      stroke={CHART_AXIS}
                      tick={{ fontSize: 11 }}
                      tickFormatter={formatChartCurrency}
                    />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number | string, name) => [
                        value === null || value === undefined ? '—' : formatCurrency(Number(value)),
                        name,
                      ]}
                    />
                    <Legend />
                    <Bar
                      dataKey="monthly"
                      name="Revenue that month"
                      fill={MONTHLY_COLOR}
                      stroke={MONTHLY_STROKE}
                      strokeWidth={1}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="ttm"
                      name="Trailing twelve months"
                      stroke={TTM_COLOR}
                      strokeWidth={3}
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pace">
          <Card>
            <CardHeader>
              <CardTitle>Fiscal-year pace</CardTitle>
              <CardDescription>
                Both years start at zero on 1 March, so the vertical gap at the current month is how
                far ahead or behind {fiscalYearLabel(data.pace.currentFiscalYear)} is. This view
                only ever climbs — it shows pace, not trend.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[420px] w-full" role="img" aria-label={paceDescription}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={paceChartData} margin={DEFAULT_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS} opacity={0.2} />
                    <XAxis dataKey="label" stroke={CHART_AXIS} tick={{ fontSize: 11 }} />
                    <YAxis
                      stroke={CHART_AXIS}
                      tick={{ fontSize: 11 }}
                      tickFormatter={formatChartCurrency}
                    />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number | string, name) => [
                        value === null || value === undefined ? '—' : formatCurrency(Number(value)),
                        name,
                      ]}
                    />
                    <Legend />
                    {currentFyIndex !== null && (
                      <ReferenceLine
                        x={fiscalLabel(currentFyIndex)}
                        stroke={CHART_AXIS}
                        strokeDasharray="4 4"
                        label={{
                          value: 'Latest complete month',
                          position: 'insideTopRight',
                          fontSize: 11,
                          fill: CHART_AXIS,
                        }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="prior"
                      name={fiscalYearLabel(data.pace.priorFiscalYear)}
                      stroke={PRIOR_COLOR}
                      strokeWidth={2}
                      // Dashed as well as differently coloured, so the two years are
                      // distinguishable without relying on hue.
                      strokeDasharray="6 4"
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="current"
                      name={fiscalYearLabel(data.pace.currentFiscalYear)}
                      stroke={TTM_COLOR}
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {showTable && (
        <Card>
          <CardHeader>
            <CardTitle>Monthly detail</CardTitle>
            <CardDescription>
              The figures behind both charts. TTM is blank for the earliest eleven months, which do
              not have twelve months of history behind them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <caption className="sr-only">
                  {category} revenue by month, with the trailing-twelve-month total and the
                  fiscal-year running total. Values in CAD.
                </caption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Month</TableHead>
                    <TableHead scope="col" className="text-right">
                      Revenue
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      Deals
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      Trailing 12 months
                    </TableHead>
                    <TableHead scope="col">Fiscal year</TableHead>
                    <TableHead scope="col" className="text-right">
                      FY running total
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...data.months].reverse().map((m) => (
                    <TableRow key={m.month_key}>
                      <TableHead scope="row" className="font-normal">
                        {monthLabel(m.month_key)}
                        {!m.complete && (
                          <span className="ml-2 text-xs text-muted-foreground">(in progress)</span>
                        )}
                      </TableHead>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(m.revenue)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{m.deal_count}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {m.ttm === null ? '—' : formatCurrency(m.ttm)}
                      </TableCell>
                      <TableCell>
                        {fiscalYearLabel(m.fiscal_year)}{' '}
                        <span className="text-xs text-muted-foreground">
                          ({fiscalLabel(m.fiscal_month_index)})
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(m.fy_cumulative)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4" aria-hidden="true" />
            What this counts, and what it leaves out
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Software and Services can only be told apart from a product family, so this report
            counts <strong>line-item revenue</strong> on Closed Won opportunities. It will therefore
            not match the dashboard cards that sum the opportunity amount instead.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Deals with no line items are invisible here — including the &ldquo;Service (Do not
              Use)&rdquo; forecast placeholders, which carry no products.
            </li>
            <li>
              Where a deal&rsquo;s amount disagrees with the sum of its own lines, this follows the
              lines.
            </li>
            <li>
              Services is everything that is not one of the eight Software families, including
              products with no family set, so the two categories together cover every line item.
            </li>
            <li>
              The current month is still accumulating. It is shown as a separate figure and kept out
              of the TTM line, where a part-month would create a false downturn.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
